import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Plus, Pencil } from "lucide-react"
import { toast } from "sonner"
import { api, type ApiEnvelope, type BlogPost } from "@/lib/api"
import { getErrorMessage, getFieldErrors } from "@/lib/get-error-message"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { FieldError } from "@/components/ui/field-error"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { ConfirmDelete } from "@/components/admin/confirm-delete"
import { ImageUpload } from "@/components/admin/image-upload"
import { RichTextEditor } from "@/components/admin/rich-text-editor"

export const Route = createFileRoute("/admin/_authed/blog/")({
  component: BlogList,
})

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
}

type FormState = {
  title: string
  slug: string
  excerpt: string
  content: string
  tagsInput: string
  authorName: string
  authorRole: string
  isPublished: boolean
  publishedAt: string
}
const empty: FormState = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  tagsInput: "",
  authorName: "",
  authorRole: "",
  isPublished: false,
  publishedAt: new Date().toISOString().slice(0, 10),
}

function BlogList() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useQuery<BlogPost[]>({
    queryKey: ["admin", "blogs"],
    queryFn: async () => (await api.get<ApiEnvelope<BlogPost[]>>("/admin/blogs")).data.data,
  })

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<BlogPost | null>(null)
  const [form, setForm] = useState<FormState>(empty)
  const [slugTouched, setSlugTouched] = useState(false)
  const [coverImage, setCoverImage] = useState<File | null>(null)
  const [authorAvatar, setAuthorAvatar] = useState<File | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "blogs"] })
    queryClient.invalidateQueries({ queryKey: ["blogs"] })
  }

  const save = useMutation({
    mutationFn: async () => {
      const fd = new FormData()
      fd.append("title", form.title)
      fd.append("slug", form.slug)
      fd.append("excerpt", form.excerpt)
      fd.append("content", form.content)
      fd.append("is_published", form.isPublished ? "1" : "0")
      fd.append("published_at", form.publishedAt)
      fd.append("author_name", form.authorName)
      fd.append("author_role", form.authorRole)
      const tags = form.tagsInput.split(",").map((t) => t.trim()).filter(Boolean)
      if (tags.length > 0) {
        tags.forEach((tag) => fd.append("tags[]", tag))
      } else {
        // Force the "tags" key to be present so clearing all tags actually
        // persists as an empty array instead of leaving the old value untouched.
        fd.append("tags[]", "")
      }
      if (coverImage) fd.append("cover_image", coverImage)
      if (authorAvatar) fd.append("author_avatar", authorAvatar)
      if (editing) {
        fd.append("_method", "PUT")
        await api.post(`/admin/blogs/${editing.slug}`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      } else {
        await api.post("/admin/blogs", fd, { headers: { "Content-Type": "multipart/form-data" } })
      }
    },
    onSuccess: () => {
      invalidate()
      setOpen(false)
      setFieldErrors({})
      toast.success(editing ? "Post updated." : "Post created.")
    },
    onError: (error) => {
      setFieldErrors(getFieldErrors(error))
      toast.error(getErrorMessage(error))
    },
  })

  const remove = useMutation({
    mutationFn: async (slug: string) => api.delete(`/admin/blogs/${slug}`),
    onSuccess: () => {
      invalidate()
      toast.success("Blog post deleted.")
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const openCreate = () => {
    setEditing(null)
    setForm(empty)
    setSlugTouched(false)
    setCoverImage(null)
    setAuthorAvatar(null)
    setFieldErrors({})
    setOpen(true)
  }
  const openEdit = (post: BlogPost) => {
    setEditing(post)
    setSlugTouched(true)
    setForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt ?? "",
      content: post.content ?? "",
      tagsInput: (post.tags ?? []).join(", "),
      authorName: post.author_name ?? "",
      authorRole: post.author_role ?? "",
      isPublished: post.is_published,
      publishedAt: post.published_at ? post.published_at.slice(0, 10) : new Date().toISOString().slice(0, 10),
    })
    setCoverImage(null)
    setAuthorAvatar(null)
    setFieldErrors({})
    setOpen(true)
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Blog</h1>
          <p className="mt-1 text-sm text-muted-foreground">Posts shown on the public blog.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-full">
              <Plus className="h-4 w-4" /> New post
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit blog post" : "New blog post"}</DialogTitle>
            </DialogHeader>
            <form
              className="space-y-4"
              noValidate
              onSubmit={(e) => {
                e.preventDefault()
                save.mutate()
              }}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={form.title}
                    onChange={(e) => {
                      const title = e.target.value
                      setForm((f) => ({ ...f, title, slug: slugTouched ? f.slug : slugify(title) }))
                    }}
                    aria-invalid={!!fieldErrors.title}
                  />
                  <FieldError message={fieldErrors.title} />
                </div>
                <div>
                  <Label htmlFor="slug">Slug</Label>
                  <Input
                    id="slug"
                    value={form.slug}
                    onChange={(e) => {
                      setSlugTouched(true)
                      setForm({ ...form, slug: e.target.value })
                    }}
                    aria-invalid={!!fieldErrors.slug}
                  />
                  <FieldError message={fieldErrors.slug} />
                </div>
              </div>
              <div>
                <Label htmlFor="excerpt">Excerpt</Label>
                <Textarea
                  id="excerpt"
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  aria-invalid={!!fieldErrors.excerpt}
                />
                <FieldError message={fieldErrors.excerpt} />
              </div>
              <ImageUpload label="Cover image" currentUrl={editing?.cover_image_url} onChange={setCoverImage} />
              <div>
                <Label htmlFor="tags">Tags</Label>
                <Input
                  id="tags"
                  value={form.tagsInput}
                  onChange={(e) => setForm({ ...form, tagsInput: e.target.value })}
                  placeholder="e.g. AI, Web Development, SaaS"
                />
                <p className="mt-1 text-xs text-muted-foreground">Comma separated.</p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="author_name">Author name</Label>
                  <Input id="author_name" value={form.authorName} onChange={(e) => setForm({ ...form, authorName: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="author_role">Author role</Label>
                  <Input id="author_role" value={form.authorRole} onChange={(e) => setForm({ ...form, authorRole: e.target.value })} />
                </div>
              </div>
              <ImageUpload
                label="Author avatar"
                currentUrl={editing?.author_avatar_url}
                onChange={setAuthorAvatar}
                rounded="rounded-full"
              />
              <div>
                <Label>Content</Label>
                <div className="mt-2">
                  <RichTextEditor value={form.content} onChange={(content) => setForm((f) => ({ ...f, content }))} />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 sm:items-end">
                <div>
                  <Label htmlFor="published_at">Published date</Label>
                  <Input
                    id="published_at"
                    type="date"
                    value={form.publishedAt}
                    onChange={(e) => setForm({ ...form, publishedAt: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-3 pb-2">
                  <Switch
                    checked={form.isPublished}
                    onCheckedChange={(v) => setForm({ ...form, isPublished: v })}
                    id="is_published"
                  />
                  <Label htmlFor="is_published">Published</Label>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? "Saving..." : "Save"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 rounded-2xl border border-border bg-card">
        {isLoading ? (
          <div className="h-40 animate-pulse rounded-2xl bg-secondary" />
        ) : isError ? (
          <p className="p-6 text-sm text-destructive">Couldn't load blog posts.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Published</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data ?? []).map((post) => (
                <TableRow key={post.id}>
                  <TableCell>{post.title}</TableCell>
                  <TableCell>
                    <Badge variant={post.is_published ? "default" : "secondary"}>
                      {post.is_published ? "Published" : "Draft"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {post.published_at ? new Date(post.published_at).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(post)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <ConfirmDelete itemLabel={post.title} onConfirm={() => remove.mutate(post.slug)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}
